// Sentry test
import * as Sentry from '@sentry/react'
import axios from 'axios'
import toastCaller, { HANDLED_SERVER_ERROR_CODES } from '../utils/toastCaller'
import {
	GENERIC_SERVER_ERROR_MESSAGE,
	SERVER_ERROR_TOAST_ID,
} from '../utils/handleApiError'
import { AuthService, isLoginRedirectStarted } from './auth.services'
import { isPublicAuthExcludedPathname } from '../utils/authPath'

const isLocalhost = () => {
	if (typeof window === 'undefined') return false
	const hostname = window.location.hostname
	return hostname === 'localhost' || hostname === '127.0.0.1'
}

// Local dev only: proxy cannot read .solarion.ai cookies — pass session via env.
const applyLocalDevSessionHeaders = (config: {
	headers: Record<string, unknown>
}) => {
	if (!isLocalhost()) return config

	const eSignToken =
		config.headers['X-ESign-Token'] ?? config.headers['x-esign-token']
	if (eSignToken) return config

	const dmsSession = process.env.REACT_APP_DEV_DMS_SESSION?.trim()
	const xsrfToken = process.env.REACT_APP_DEV_XSRF_TOKEN?.trim()

	if (dmsSession && !config.headers['X-Dms-Session']) {
		config.headers['X-Dms-Session'] = dmsSession
	}

	if (xsrfToken && !config.headers['X-XSRF-TOKEN']) {
		config.headers['X-XSRF-TOKEN'] = xsrfToken
	}

	return config
}

const api = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_API,
	withCredentials: true,
})

export const apiNode = axios.create({
	baseURL: process.env.REACT_APP_NODE_BACKEND_API,
	withCredentials: true,
})

const hasHandledServerErrorCode = (error: any): boolean => {
	const errorCodes = error?.response?.data?.errors
	if (!Array.isArray(errorCodes)) return false

	return errorCodes.some((code: string) =>
		HANDLED_SERVER_ERROR_CODES.includes(code),
	)
}

const isAuthMeRequest = (url?: string) => {
	if (!url) return false
	return /\/auth\/me(\?|$)/.test(url)
}

const hasESignTokenHeader = (headers?: Record<string, unknown>) => {
	if (!headers) return false
	return Boolean(headers['X-ESign-Token'] ?? headers['x-esign-token'])
}

const isCurrentPublicAuthExcludedPath = () => {
	if (typeof window === 'undefined') return false
	return isPublicAuthExcludedPathname(window.location.pathname)
}

const handleUnauthorizedResponse = (error: any) => {
	if (error?.response?.status !== 401) return
	if (isAuthMeRequest(error?.config?.url)) return
	if (isCurrentPublicAuthExcludedPath()) return
	if (hasESignTokenHeader(error?.config?.headers)) return
	if (isLoginRedirectStarted()) return

	void AuthService.revalidateSessionOrLogin()
}

apiNode.interceptors.request.use((config) => {
	applyLocalDevSessionHeaders(config)
	return config
})

api.interceptors.response.use(
	(response) => response,
	(error) => {
		handleUnauthorizedResponse(error)

		if (error.response?.status === 500) {
			toastCaller({
				type: 'error',
				message: GENERIC_SERVER_ERROR_MESSAGE,
				toastId: SERVER_ERROR_TOAST_ID,
				update: true,
			})
		}
		return Promise.reject(error)
	},
)
export default api

// 500 errors handler
apiNode.interceptors.response.use(
	(response) => response,
	(error) => {
		handleUnauthorizedResponse(error)

		if (error.response?.status === 500 && !hasHandledServerErrorCode(error)) {
			toastCaller({
				type: 'error',
				withClosePrevious: true,
				message: GENERIC_SERVER_ERROR_MESSAGE,
				toastId: SERVER_ERROR_TOAST_ID,
				update: true,
			})
		}
		return Promise.reject(error)
	},
)

// sentry
apiNode.interceptors.response.use(
	(response) => {
		const urlParts = response.config.url?.split('/') || []
		const v2Index = urlParts.findIndex((part: string) => part === 'v2')
		const clearURL =
			v2Index !== -1
				? urlParts.slice(v2Index + 1).join('/')
				: response.config.url

		Sentry.addBreadcrumb({
			category: 'api',
			message: `✅ [${
				response.status
			}] ${response.config.method?.toUpperCase()} ${clearURL}`,
			level: 'info',
			data: {
				method: response.config.method?.toUpperCase(),
				endpoint: clearURL,
				status: response.status,
				type: 'api-response',
				timestamp: new Date().toISOString(),
				responseData: response.data,
			},
		})

		return response
	},
	(error) => {
		const urlParts = error.config?.url?.split('/') || []
		const v2Index = urlParts.findIndex((part: string) => part === 'v2')
		const clearURL =
			v2Index !== -1 ? urlParts.slice(v2Index + 1).join('/') : error.config?.url

		Sentry.addBreadcrumb({
			category: 'api',
			message: `🔴 [${
				error.response?.status || 'ERR'
			}] ${error.config?.method?.toUpperCase()} ${clearURL}`,
			level: 'error',
			data: {
				method: error.config?.method?.toUpperCase(),
				endpoint: clearURL,
				status: error.response?.status,
				type: 'api-error',
				timestamp: new Date().toISOString(),
				requestData: error.config?.data,
				requestParams: error.config?.params,
				responseData: error.response?.data,
			},
		})

		Sentry.captureMessage(
			`🔴 ${
				error.response?.status
			} ${error.config?.method?.toUpperCase()} ${clearURL}`,
			{
				level: 'error',
				tags: {
					action: 'api_error',
					method: error.config?.method?.toUpperCase(),
					endpoint: clearURL,
					status: error.response?.status,
				},
				extra: {
					url: window.location.pathname,
					timestamp: new Date().toISOString(),
					error_message: error.message,
					response_data: error.response?.data,
				},
			},
		)

		return Promise.reject(error)
	},
)

apiNode.interceptors.request.use((config) => {
	applyLocalDevSessionHeaders(config)

	const urlParts = config.url?.split('/') || []
	const v2Index = urlParts.findIndex((part: string) => part === 'v2')
	const clearURL =
		v2Index !== -1 ? urlParts.slice(v2Index + 1).join('/') : config.url

	Sentry.addBreadcrumb({
		category: 'api',
		message: `🚀 [REQUEST] ${config.method?.toUpperCase()} ${clearURL}`,
		level: 'info',
		data: {
			method: config.method?.toUpperCase(),
			endpoint: clearURL,
			type: 'api-request',
			timestamp: new Date().toISOString(),
			requestData: config.data,
			requestParams: config.params,
		},
	})

	return config
})
